import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { InterceptorUtil, OCC_USER_ID_ANONYMOUS, OccEndpointsService, USE_CLIENT_TOKEN } from '@spartacus/core';
import { USER_PROFILE_NORMALIZER, USER_PROFILE_SERIALIZER, USER_SIGN_UP_SERIALIZER } from '@spartacus/user/profile/core';
import { OccUserProfileAdapter } from '@spartacus/user/profile/occ';
import { Observable, of } from 'rxjs';
import { catchError, mapTo } from 'rxjs/operators';
import { Credentials, NameInputCheckResult, User, UserInputCheck, UserSignUp } from '../../model';
import { Occ } from '../../occ';
import { ConverterService } from '../../util';

@Injectable({
  providedIn: 'root',
})
export class UserProfileAdapter extends OccUserProfileAdapter {
  constructor(protected http: HttpClient, protected occEndpoints: OccEndpointsService, protected converter: ConverterService) {
    super(http, occEndpoints, converter);
  }

  load(userId: string): Observable<User> {
    const url = this.occEndpoints.buildUrl('user', { urlParams: { userId } });
    return this.http.get<Occ.User>(url).pipe(this.converter.pipeable(USER_PROFILE_NORMALIZER));
  }

  update(userId: string, user: User): Observable<{}> {
    const url = this.occEndpoints.buildUrl('user', { urlParams: { userId } });
    user = this.converter.convert(user, USER_PROFILE_SERIALIZER);
    return this.http.patch(url, user);
  }

  register(user: UserSignUp): Observable<User> {
    const url: string = this.occEndpoints.buildUrl('userRegister');
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true, headers);
    user = this.converter.convert(user, USER_SIGN_UP_SERIALIZER);

    return this.http.post<User>(url, user, { headers }).pipe(this.converter.pipeable(USER_PROFILE_NORMALIZER));
  }

  requestForgotPasswordEmail(userEmailAddress: string, username?: string): Observable<{}> {
    const queryParams = { email: userEmailAddress };
    const url = this.occEndpoints.buildUrl('userForgotPassword', {
      queryParams: !username
        ? queryParams
        : {
            ...queryParams,
            username: username,
          },
    });
    const headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true);

    return this.http.post(url, {}, { headers });
  }

  resetPassword(token: string, newPassword: string): Observable<{}> {
    const url = this.occEndpoints.buildUrl('userResetPassword');
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true, headers);

    return this.http.post(url, { token, newPassword }, { headers });
  }

  requestForgotUsernameEmail(userEmailAddress: string): Observable<{}> {
    const queryParams = { email: userEmailAddress };
    const url = this.occEndpoints.buildUrl('userForgotUsername', { queryParams });
    const headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true);
    return this.http.post(url, {}, { headers });
  }

  updateCredentials(userId: string, credentials: Credentials): Observable<{}> {
    const url = this.occEndpoints.buildUrl('credentials', { urlParams: { userId } });
    return this.http.patch(url, credentials);
  }

  remove(userId: string, password?: string): Observable<{}> {
    const url = this.occEndpoints.buildUrl('user', { urlParams: { userId }, queryParams: { password } });
    return this.http.delete(url);
  }

  validateUserEmail(userId: string, email: string): Observable<UserInputCheck> {
    const url = this.occEndpoints.buildUrl('registrationEmailCheck', {
      queryParams: { email },
    });

    let headers = new HttpHeaders();

    if (userId === OCC_USER_ID_ANONYMOUS) {
      headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true, headers);
    }

    return this.http.get(url, { headers }).pipe(
      mapTo({ valid: true, message: '' }),
      catchError((res) => {
        return of({ valid: false, message: res.error?.errors[0]?.message });
      })
    );
  }

  checkDuplicatedUsername(username: string): Observable<UserInputCheck> {
    const url = this.occEndpoints.buildUrl('registrationUsernameDuplicateCheck', {
      queryParams: { username },
    });

    const headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true);
    return this.http.get(url, { headers }).pipe(
      mapTo({ valid: true, message: '' }),
      catchError((res) => {
        return of({ valid: false, message: res.error?.errors[0]?.message });
      })
    );
  }

  validateName(name: string): Observable<NameInputCheckResult> {
    const url = this.occEndpoints.buildUrl('nameCheck', {
      queryParams: { name: name },
    });
    const headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true);
    return this.http.get(url, { headers }).pipe(
      mapTo({ valid: true, message: '' }),
      catchError((res) => {
        return of({ valid: false, message: res.error?.errors[0]?.message });
      })
    );
  }

  changeCustomerSessionLanguage(userId: string, languageCode: string): Observable<any> {
    const url = this.occEndpoints.buildUrl('changeCustomerSessionLanguage', {
      urlParams: { userId },
      queryParams: { isoCode: languageCode },
    });
    return this.http.post(url, null);
  }
}
